import {
  createAnchor,
  createImgTag,
  wrapP,
  wrapTag,
} from '../../../ingestion/html_utility'
import {
  oseMaterialCodesAtom,
  tableUpdateConfigAtom,
  unitCode,
  useVideoCodesAtom,
  videoCode,
} from './atoms'
import { useCallback } from 'react'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'

function itemLabelMap(label: string) {
  return (
    {
      'where-going': 'Where We Are Going',
      'where-not-going': 'Where We Are NOT Going',
      'other-materials-preparation': 'Materials Preparation',
      'what-do-and-figure-out': 'What To Do And Figure Out',
      'how-represent': 'How To Represent',
      'navigate-next-lesson': 'Navigate To Next Lesson',
    }[label] ?? label
  )
}

export function useNoteNodeContentForTable() {
  const tableParser = useTableData()
  return useCallback(
    (table: HTMLTableElement) => {
      const data = tableParser(table)
      return Object.entries(data).reduce((acc, [key, value], index) => {
        return (
          acc +
          ((!!index && wrapP('<br data-mce-bogus="1">')) || '') +
          wrapP(key) +
          wrapP('<br data-mce-bogus="1">') +
          wrapP(value)
        )
      }, '')
    },
    [tableParser],
  )
}

function useTableData() {
  const elementParser = useHtmlStringForElement()
  const updatesConfig = useAtomValue(tableUpdateConfigAtom)
  return useCallback(
    (table: HTMLTableElement) => {
      const tableKey = getTableKey(table) ?? ''
      const tableConfig = updatesConfig[tableKey]
      return Array.from(table.getElementsByTagName('tr')).reduce<
        Record<string, string>
      >((acc, row, currentIndex) => {
        if (!currentIndex) return acc
        const [thing, content] = Array.from(row.getElementsByTagName('td'))
        const itemKey = thing?.textContent?.trim() ?? ''
        if (itemKey === tableKey) return acc
        if (tableConfig?.filteredRows?.includes(itemKey)) return acc
        if (!content) return acc
        return {
          ...acc,
          [itemLabelMap(itemKey)]: elementParser(content),
        }
      }, {})
    },
    [elementParser, updatesConfig],
  )
}

function cleanElementHtml(element: HTMLElement) {
  ;['style', 'class'].forEach((attr) =>
    Array.from(element.querySelectorAll(`[${attr}]`)).forEach((p) =>
      p.setAttribute(attr, ''),
    ),
  )
  Array.from(element.children).forEach((child) => {
    const childText = child.textContent?.trim()
    if (!childText) {
      child.innerHTML = '<br data-mce-bogus="1">'
    } else if (childText.match(/^\w{1,5}\.\w{1,5}\.\w{1,5}\.\d{1,5}$/)) {
      child.innerHTML = tagForImageCode(childText)
    }
  })
}

function useHtmlStringForElement() {
  const unitCodes = useAtomValue(oseMaterialCodesAtom)
  const videoCodes = useAtomValue(useVideoCodesAtom)
  return useCallback(
    (element: HTMLElement) => {
      cleanElementHtml(element)

      return [
        swapImageGroup,
        swapImageCode,
        (c: string) => swapUnitCode(c, unitCodes),
        (c: string) => swapVideoCode(c, videoCodes),
      ].reduce((acc, func) => func(acc), element.innerHTML)
    },
    [unitCodes, videoCodes],
  )
}

function swapVideoCode(
  contentHtml: string,
  videoCodes: Record<string, videoCode>,
) {
  const videoRegex = /\[video:(.*?)]/g
  return contentHtml.replaceAll(videoRegex, (matched, captureGroup) => {
    const link = videoCodes[captureGroup.trim().toLowerCase()]?.url
    if (!link) return matched
    return createAnchor({ link, display: link, newTab: true })
  })
}

function swapUnitCode(
  contentHtml: string,
  unitCodes: Record<string, unitCode>,
) {
  const materialRegex = /\[material:(.*?)]/g
  return contentHtml.replaceAll(materialRegex, (matched, captureGroup) => {
    const swapCode = captureGroup.trim().toLowerCase()
    const swapItem = unitCodes[swapCode]
    return wrapTag('i', swapItem?.title ?? matched)
  })
}

function tagForImageCode(imageCode: string, width: number = 710) {
  const link = `https://kiddom-static.s3.amazonaws.com/images/${imageCode}.jpg`
  return createImgTag({ src: link, attributes: { width: `${width}px` } })
}

function swapImageCode(contentHtml: string) {
  const imgRegex = /\[image:(.*?)]/g
  return contentHtml.replaceAll(imgRegex, (_, captureGroup) => {
    const imageCode = captureGroup.split(',')[0]
    return tagForImageCode(imageCode)
  })
}

function swapImageGroup(contentHtml: string) {
  const imageSetRegex = /\[image-set:(.*?)]/g
  return contentHtml.replaceAll(imageSetRegex, (matched, captureGroup) => {
    if (typeof captureGroup !== 'string') return matched
    const codes = captureGroup.split(',').map((code) => code.trim())
    const width = 710 / codes.length
    return codes
      .map((code) => {
        return tagForImageCode(code, width)
      })
      .join()
  })
}

export function getTableKey(table: HTMLTableElement) {
  const firstRow = table.getElementsByTagName('tr')[0]
  const [firstCol, ...rest] = Array.from(firstRow.getElementsByTagName('td'))
  if (rest.length) return
  return firstCol.textContent ?? 'NO TITLE'
}
